<script setup>
import { computed, nextTick, provide, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppShell from './components/AppShell.vue'
import AppIcon from './components/AppIcon.vue'
import PolicyDetailsDrawer from './components/PolicyDetailsDrawer.vue'
import PolicyForm from './components/PolicyForm.vue'
import ToastStack from './components/ToastStack.vue'
import { callModel } from './lib/aiClient.js'
import { loadAiSettings } from './lib/aiSettings.js'
import { hashText } from './lib/cache.js'
import { STORAGE_KEYS, storage } from './lib/storage.js'
import { usePolicyStore } from './stores/policyStore.js'
import AiView from './views/AiView.vue'
import AnalysisView from './views/AnalysisView.vue'
import BackupView from './views/BackupView.vue'
import SettingsView from './views/SettingsView.vue'
import { useModalLayer } from './composables/useModalLayer.js'

const store = usePolicyStore()
const route = useRoute()
const router = useRouter()
const formOpen = ref(false)
const editingPolicy = ref(null)
const detailPolicy = ref(null)
const activePanel = ref('')
const optimizingNote = ref(false)
const featureRoot = ref(null)
const featureClose = ref(null)
const panelMeta = {
  ai: { eyebrow: 'AI 智能检视', title: 'AI 智能分析', label: 'AI 智能分析' },
  settings: { eyebrow: '模型设置', title: '设置', label: '设置' },
  backup: { eyebrow: '本地数据', title: '数据与备份', label: '数据备份' },
  analysis: { eyebrow: '保障目标', title: '保障目标与校准', label: '保障目标' },
}
const activePanelMeta = computed(() => panelMeta[activePanel.value] || panelMeta.analysis)

const openPolicyForm = (policy = null) => { editingPolicy.value = policy ? JSON.parse(JSON.stringify(policy)) : null; formOpen.value = true }
const closePolicyForm = () => { formOpen.value = false; editingPolicy.value = null }
const savePolicy = (policy) => { store.savePolicy(policy); closePolicyForm() }
const openPolicyDetails = (policy) => { detailPolicy.value = policy }
const closePanel = () => { activePanel.value = ''; if (route.path !== '/') router.replace('/') }
const openPanel = (panel) => { activePanel.value = panel }

useModalLayer({
  rootRef: featureRoot,
  initialFocusRef: featureClose,
  onClose: closePanel,
  active: computed(() => Boolean(activePanel.value)),
})

watch(() => route.path, async (path) => {
  const panelByPath = { '/analysis': 'analysis', '/ai': 'ai', '/backup': 'backup', '/settings': 'settings' }
  if (panelByPath[path]) activePanel.value = panelByPath[path]
  if (path === '/policies') {
    activePanel.value = ''
    await nextTick()
    document.querySelector('#family-policies')?.scrollIntoView({ behavior: 'smooth' })
  }
}, { immediate: true })

const optimizeNote = async (form) => {
  const source = String(form.note || '').split('\n\n-----------------\n✨ AI 解读总结：\n')[0].trim()
  if (!source) return
  const settings = loadAiSettings()
  if (!settings.apiKey) { store.addNotice('请先配置 AI API Key。','warning');openPanel('settings');return }
  if (!confirm('将把备注框中的完整文本发送给所选模型服务商。文本可能包含健康或病历信息，确定继续吗？')) return
  const key = `hx_ai_note_cache:v3:${settings.provider}:${settings.model}:${hashText(source)}`
  let summary = storage.get(key)
  optimizingNote.value = true
  try {
    if (!summary) {
      summary = await callModel({ ...settings, systemPrompt: '你是一位熟悉中国大陆保险条款的专业顾问。严格基于原文，用通俗中文输出：主要含义、对客户有利的点、限制和免责、理赔条件、需要人工确认的问题。不得承诺理赔，不得补充原文没有的信息。', prompt: `请解读以下保险条款或记录：\n\n${source}` })
      storage.set(key, summary)
      const index = storage.json(STORAGE_KEYS.noteCacheIndex, [])
      const next = [key, ...index.filter((item) => item !== key)]
      next.slice(50).forEach((item) => storage.remove(item))
      storage.set(STORAGE_KEYS.noteCacheIndex, JSON.stringify(next.slice(0, 50)))
    }
    form.note = `${source}\n\n-----------------\n✨ AI 解读总结：\n${summary}`
    store.addNotice('条款解读已加入备注，请核对后保存。','success')
  } catch (error) { store.addNotice(error.message,'danger') } finally { optimizingNote.value = false }
}

provide('openPolicyForm', openPolicyForm)
provide('openPolicyDetails', openPolicyDetails)
provide('openFeaturePanel', openPanel)
</script>

<template>
  <AppShell data-app-content @new-policy="openPolicyForm()" @open-panel="openPanel"><RouterView/></AppShell>
  <PolicyForm v-if="formOpen" :policy="editingPolicy" :optimizing-note="optimizingNote" @close="closePolicyForm" @save="savePolicy" @optimize-note="optimizeNote"/>
  <PolicyDetailsDrawer v-if="detailPolicy" :policy="detailPolicy" @close="detailPolicy=null" @edit="detailPolicy=null;openPolicyForm($event)"/>
  <section v-if="activePanel" ref="featureRoot" class="feature-overlay" role="dialog" aria-modal="true" :aria-label="activePanelMeta.label" tabindex="-1">
    <button class="feature-backdrop" type="button" aria-label="关闭面板" @click="closePanel"/>
    <div class="feature-panel" :class="`feature-panel-${activePanel}`">
      <header><div><span>{{ activePanelMeta.eyebrow }}</span><h2>{{ activePanelMeta.title }}</h2></div><button ref="featureClose" class="feature-close" type="button" aria-label="关闭" @click="closePanel"><AppIcon name="close" tone="neutral"/></button></header>
      <div class="feature-panel-body"><AiView v-if="activePanel==='ai'"/><SettingsView v-else-if="activePanel==='settings'"/><BackupView v-else-if="activePanel==='backup'"/><AnalysisView v-else/></div>
    </div>
  </section>
  <ToastStack/>
</template>
